import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Form, Formik, useFormikContext } from 'formik';
import { Button, Grid } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';
import { getFarmPolicyMask } from 'common/helpers/farm-helpers';

import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import { useDisabledState } from 'common/disabled-context';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import CountiesAutocomplete from 'common/components/counties-autocomplete';
import AddressAutocomplete from 'core/components/address-autocomplete/address-autocomplete';
import { NotificationCard } from 'core/components/notification-card';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import { AuthContext } from 'core/components/auth';

import { validationSchema } from './add-location.validations';
import useStyles from './location.styles';

const initialValues = {
  legalDescription: {
    format: undefined,
    section: undefined,
    township: undefined,
    townshipCoordinates: undefined,
    range: undefined,
    rangeCoordinates: undefined,
    lot: undefined,
    block: undefined
  },
  description: undefined,
  numberOfAcres: undefined,
  address: {
    address: undefined,
    address2: undefined,
    city: undefined,
    state: undefined,
    zip: undefined,
    county: undefined
  }
};

const { coordinateOptions } = lookupsJson;
const { legalDescriptionFormatOptions } = lookupsJson.farm;

// Wrapper component to access parent Formik context and validation modal
const AddLocationWithValidation = observer(({ onAdd, disabled }) => {
  const parentFormik = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();
  const session = useContext(AuthContext);

  return (
    <>
      <AddLocationInternal
        onAdd={onAdd}
        disabled={disabled}
        parentValidateForm={parentFormik.validateForm}
        parentSetErrors={parentFormik.setErrors}
        parentSetTouched={parentFormik.setTouched}
        setShowValidationErrorsModal={setShowValidationErrorsModal}
        session={session}
      />
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
    </>
  );
});

const AddLocationInternal = observer(
  ({
    onAdd,
    disabled,
    parentValidateForm,
    parentSetErrors,
    parentSetTouched,
    setShowValidationErrorsModal,
    session
  }) => {
    const classes = useStyles();
    const { activeForm } = useDisabledState();
    // #32207: route-aware product + state (policy store on the policy route where the offer store is
    // empty) so the max-acres block (Home/Rental cap at 40) applies on the policy route too.
    const { farmPolicyType, state } = useFarmPolicyContext();

    const mask = getFarmPolicyMask(farmPolicyType, state);
    const maxAcres = mask?.maxAcres;
    // TODO: When disableAddressToggle is true (Y type), default the location address
    // to the primary applicant address and prevent the user from changing it.

    const disableForm = Boolean(activeForm || disabled);

    return (
      <Formik
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        validate={(values) => {
          try {
            validationSchema(session, maxAcres).validateSync(values, { abortEarly: false, context: values });
            return {};
          } catch (err) {
            const errors = {};
            err.inner.forEach((error) => {
              if (error.path) {
                errors[error.path] = error.message;
              }
            });
            return errors;
          }
        }}
        onSubmit={async (values, actions) => {
          // First validate the parent form (entire offer/policy)
          const parentErrors = await parentValidateForm();
          if (Object.keys(parentErrors).length > 0) {
            // Set errors and show validation modal
            parentSetErrors(parentErrors);
            // Convert errors object to touched structure (boolean flags instead of error messages)
            const touched = setTouchedFromErrors(parentErrors);
            parentSetTouched(touched, false);
            setShowValidationErrorsModal(true);
            return;
          }

          // If parent validation passes, proceed with adding the location
          // Filter out street address fields for non-street legal description formats
          const legalDescriptionFormat = values?.legalDescription?.format;
          if (legalDescriptionFormat !== 'street' && values.address) {
            const filteredValues = {
              ...values,
              address: {
                ...values.address,
                address: null,
                address2: null
              }
            };
            onAdd(filteredValues);
          } else {
            onAdd(values);
          }
          actions.resetForm();
        }}
      >
        {({ values, setFieldValue }) => {
          const legalDescriptionFormat = values?.legalDescription?.format;
          // Get state from appConfig for CountiesAutocomplete (GitHub issue #28634)
          // Farm brands are single-state, so we use the first state in appConfig.farm
          const farmState = values?.address?.state || Object.keys(appConfig.farm || {})[0] || 'IL';
          return (
            <Form className={classes.container}>
              <Label type="infoSubtitle">Add location</Label>
              <Grid key="container" container justifyContent="space-between" alignItems="center" spacing={2}>
                <Field
                  xs={4}
                  type="select"
                  name="legalDescription.format"
                  mode="light"
                  label="Legal description format*"
                  options={legalDescriptionFormatOptions}
                  onChange={(value) => {
                    // Default legalDescriptionFormat values
                    if (value === 'section-township-range') {
                      setFieldValue('legalDescription.townshipCoordinates', 'unknown');
                      setFieldValue('legalDescription.rangeCoordinates', 'unknown');
                    }
                  }}
                  disabled={disableForm}
                />
                <Field xs={4} type="numeric" name="numberOfAcres" mode="light" label="Acres*" disabled={disableForm} />
                <Field
                  xs={4}
                  type="string"
                  name="description"
                  mode="light"
                  label="Location name*"
                  disabled={disableForm}
                />

                {legalDescriptionFormat === 'street' && (
                  <>
                    <AddressAutocomplete
                      id="address.address"
                      name="address.address"
                      xs={12}
                      mode="light"
                      label="Address*"
                      disabled={disableForm}
                      riskAddress
                    />
                    <Field
                      xs={12}
                      type="string"
                      name="address.address2"
                      mode="light"
                      label="Apt, unit, building, etc."
                      disabled={disableForm}
                    />
                    <Field xs={4} type="string" name="address.city" mode="light" label="City*" disabled={disableForm} />
                    <Field
                      xs={4}
                      type="state"
                      name="address.state"
                      mode="light"
                      label="State*"
                      disabled={disableForm}
                    />
                    <Field
                      xs={4}
                      type="string"
                      name="address.zip"
                      mode="light"
                      label="Zip code*"
                      disabled={disableForm}
                    />
                  </>
                )}

                {legalDescriptionFormat === 'section-township-range' && (
                  <>
                    <Field xs={4} type="string" name="address.city" mode="light" label="City*" disabled={disableForm} />
                    <Field
                      xs={4}
                      type="state"
                      name="address.state"
                      mode="light"
                      label="State*"
                      disabled={disableForm}
                    />
                    <Field
                      xs={4}
                      type="string"
                      name="address.zip"
                      mode="light"
                      label="Zip code*"
                      disabled={disableForm}
                    />
                    <CountiesAutocomplete
                      id="address.county"
                      state={farmState}
                      setValues={setFieldValue}
                      disabled={disableForm}
                    />
                    <Field
                      xs={4}
                      type="string"
                      name="legalDescription.section"
                      mode="light"
                      label="Section*"
                      disabled={disableForm}
                    />
                    <Field
                      xs={2}
                      type="string"
                      name="legalDescription.township"
                      mode="light"
                      label="Township*"
                      disabled={disableForm}
                    />
                    <Field
                      xs={2}
                      type="select"
                      name="legalDescription.townshipCoordinates"
                      mode="light"
                      label="Coordinates"
                      options={coordinateOptions}
                      disabled={disableForm}
                    />
                    <Field
                      xs={2}
                      type="string"
                      name="legalDescription.range"
                      mode="light"
                      label="Range*"
                      disabled={disableForm}
                    />
                    <Field
                      xs={2}
                      type="select"
                      name="legalDescription.rangeCoordinates"
                      mode="light"
                      label="Coordinates"
                      options={coordinateOptions}
                      disabled={disableForm}
                    />
                  </>
                )}

                {legalDescriptionFormat === 'lot-block' && (
                  <>
                    <Field
                      xs={6}
                      type="numeric"
                      name="legalDescription.lot"
                      mode="light"
                      label="Lot*"
                      disabled={disableForm}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      name="legalDescription.block"
                      mode="light"
                      label="Block*"
                      disabled={disableForm}
                    />
                    <Field xs={4} type="string" name="address.city" mode="light" label="City*" disabled={disableForm} />
                    <Field
                      xs={4}
                      type="state"
                      name="address.state"
                      mode="light"
                      label="State*"
                      disabled={disableForm}
                    />
                    <Field
                      xs={4}
                      type="string"
                      name="address.zip"
                      mode="light"
                      label="Zip code*"
                      disabled={disableForm}
                    />
                    <CountiesAutocomplete
                      state={'IL'}
                      id="address.county"
                      setValues={setFieldValue}
                      disabled={disableForm}
                    />
                  </>
                )}

                {legalDescriptionFormat === 'long-legal' && (
                  <>
                    <Field xs={4} type="string" name="address.city" mode="light" label="City*" disabled={disableForm} />
                    <Field
                      xs={4}
                      type="state"
                      name="address.state"
                      mode="light"
                      label="State*"
                      disabled={disableForm}
                    />
                    <Field
                      xs={4}
                      type="string"
                      name="address.zip"
                      mode="light"
                      label="Zip code*"
                      disabled={disableForm}
                    />
                    <CountiesAutocomplete
                      state={'IL'}
                      setValues={setFieldValue}
                      id="address.county"
                      disabled={disableForm}
                    />
                  </>
                )}

                {!!legalDescriptionFormat && (
                  <>
                    <Field
                      xs={12}
                      type="textarea"
                      name="additionalLegalDescription"
                      mode="light"
                      label="Additional legal description (optional)"
                      minRows={3}
                      disabled={disableForm}
                      rows={3}
                    />
                    <Field
                      xs={12}
                      type="textarea"
                      name="locationDirections"
                      mode="light"
                      label="Location directions (optional)"
                      minRows={3}
                      disabled={disableForm}
                      rows={3}
                    />
                    <Grid item container justifyContent="flex-end" xs={12}>
                      <Button
                        type="submit"
                        key="addButton"
                        mode="big"
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        disabled={disableForm}
                      >
                        Add location
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
              {disableForm && (
                <NotificationCard className={classes.notification} iconSize="icon16" type="light" noShadow>
                  You can't add a location until you update the offer.
                </NotificationCard>
              )}
            </Form>
          );
        }}
      </Formik>
    );
  }
);

AddLocationWithValidation.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

AddLocationWithValidation.defaultProps = {
  disabled: false
};

AddLocationInternal.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  parentValidateForm: PropTypes.func.isRequired,
  parentSetErrors: PropTypes.func.isRequired,
  parentSetTouched: PropTypes.func.isRequired,
  setShowValidationErrorsModal: PropTypes.func.isRequired,
  session: PropTypes.object
};

export default AddLocationWithValidation;
